import { uuid } from '@elfsight/lib-utils';
import { SourceTypes } from '../../../../constants';
import { buildProxyURL, formatText, handleResponseError } from '../../utils';
import type { DataValue, ProviderType } from '../types';
import type { RSSResponseType, RSSSourceValue } from './types';

const PAGE_LENGTH = 21;
const PLATFORM = SourceTypes.RSS;

const select = (response: RSSResponseType): DataValue[] => {
  handleResponseError(response, PLATFORM, 'channel.item');

  const { channel } = response;

  if (!channel) {
    return [];
  }

  const user = {
    name: typeof channel.title === 'string' ? channel.title : undefined,
    image: channel.image?.url,
    url: channel.image?.link
  };

  if (channel.item && !Array.isArray(channel.item)) {
    channel.item = [channel.item];
  }

  return channel.item.map(
    ({ description, link, pubDate, created, enclosure, title }) => {
      const preparedEnclosure = Array.isArray(enclosure)
        ? enclosure[0]
        : enclosure;

      let media;

      if (preparedEnclosure?.type === 'video') {
        media = {
          type: preparedEnclosure.type,
          src: preparedEnclosure.link,
          thumbnail: preparedEnclosure.thumbnail || ''
        };
      } else if (preparedEnclosure?.type) {
        media = {
          type: preparedEnclosure.type,
          src: preparedEnclosure.link
        };
      }

      const formattedTitle = title?.length ? formatText(title, PLATFORM)! : '';
      const formattedDescription = description?.length
        ? formatText(description, PLATFORM)!
        : '';

      return {
        id: uuid(),
        source: {
          type: PLATFORM,
          url: link
        },
        title: formattedTitle,
        text: formattedDescription,
        timestamp: Date.parse(pubDate ?? created) || 0,
        user,
        media
      };
    }
  );
};

export function createRssFileProvider(
  sourceValue: RSSSourceValue,
  globalKey: string[],
  filtersKey: string[]
): ProviderType<RSSResponseType> {
  const { source = '' } = sourceValue;
  const key = [...globalKey, ...filtersKey, source];

  const page = (data: RSSResponseType | undefined) => {
    if (data && 'error' in data) {
      return undefined;
    }

    const { channel, paging } = data ?? {};

    if (Array.isArray(channel?.item) && channel?.item?.length < PAGE_LENGTH) {
      return undefined;
    }

    return buildProxyURL({
      apiSource: PLATFORM,
      params: {
        q: source,
        count: `${PAGE_LENGTH}`,
        cursor: `${paging?.cursor ?? 0}`
      }
    });
  };

  return { key, page, select };
}
